import _slicedToArray from "/builds/huggg/mono/node_modules/.pnpm/@babel+runtime@7.9.0/node_modules/@babel/runtime/helpers/esm/slicedToArray";
import _objectSpread from "/builds/huggg/mono/node_modules/.pnpm/@babel+runtime@7.9.0/node_modules/@babel/runtime/helpers/esm/objectSpread2";
import { path, prop, propOr } from 'ramda';
import withAxios from '../utils/with-axios';
import { urls } from './../config.js';
import { firstById } from './../utils/find';
import { hugggStatuses } from './../utils/huggg';
var V2_VOUCHER_INTEGRATION = 7;
var reformatSegmentVoucher = function reformatSegmentVoucher(voucher, segment) {
  return [{
    label: segment.name,
    value: voucher.code,
    type: 'qr'
  }, {
    label: '',
    value: voucher.code,
    type: 'alpha/qr'
  }];
};

/**
 * Transforms older vouchers into the same response as you would receive from
 * a v2 voucher request.
 */
var reformatGeneratedOriginals = function reformatGeneratedOriginals(response) {
  var huggg = response.data,
    embedded = response.embedded;
  var extract = function extract(id) {
    return propOr([], id, embedded);
  };
  var voucher = firstById(extract('vouchers'), prop('voucher_id', huggg));
  var segment = firstById(extract('product_segments'), prop('segment_id', voucher));
  var formattedSegment = segment && {
    id: segment.id,
    name: segment.name,
    voucherType: 'legacy-qr'
  };
  return {
    hugggId: huggg.id,
    segment: formattedSegment,
    redeemedSegment: formattedSegment,
    vouchers: reformatSegmentVoucher(voucher, segment),
    expiresAt: huggg.expiry_date
  };
};

/**
 * Remaps the vague "data" array to a named "vouchers" array
 *
 * @param {object} response api response from generating a voucher
 */
var reformatGeneratedV2 = function reformatGeneratedV2(response) {
  var vouchers = response.data.map(function (item) {
    item.expiresAt = response.expiresAt;
    item.voucherType = response.voucherType;
    item.fulfilmentStatus = response.fulfilmentStatus;
    return item;
  });
  return _objectSpread(_objectSpread({}, response), {}, {
    redeemedSegment: response.segment,
    vouchers: vouchers
  });
};

/**
 * Returns a function to format a response from the api. Returns a different
 * formatter depending on voucher type.
 *
 * @param {int} integrationId the integration id we're reformatting
 */
var generatedVoucherFormatter = function generatedVoucherFormatter(integrationId) {
  return integrationId === V2_VOUCHER_INTEGRATION ? reformatGeneratedV2 : reformatGeneratedOriginals;
};

/**
 * Fetches existing vouchers for a huggg
 *
 * @param {axios} api
 * @param {string} hugggId
 */
var existingvouchers = function existingvouchers(api, hugggId) {
  return api.get(urls.apiV2("/hugggs/".concat(hugggId, "/voucher/claimed"))).then(function (response) {
    return {
      vouchers: response.data.data.map(function (item) {
        return _objectSpread(_objectSpread({}, item), {}, {
          expiresAt: response.data.expiresAt,
          fulfilmentStatus: response.data.fulfilmentStatus,
          voucherType: response.data.voucherType
        });
      }),
      segment: response.data.segment
    };
  }).catch(function () {
    return {
      vouchers: []
    };
  });
}; // This is expected if no vouchers are generated.

/**
 * Fetches available voucher segments / options for a huggg
 *
 * @param {axios} api
 * @param {string} hugggId
 */
var voucherSegments = function voucherSegments(api, hugggId) {
  var isTest = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
  return api.get(urls.apiV2("/hugggs/".concat(hugggId, "/voucher/options?is_test=").concat(isTest))).then(path(['data']));
};

/**
 * Fetches available voucher segments / options for a huggg
 *
 * @param {axios} api
 * @param {string} hugggId
 */
var resendPin4 = function resendPin4(api, hugggId) {
  return api.post(urls.apiV3("/pin4cash/orders/".concat(hugggId, "/resend"))).then(path(['data']));
};

/**
 * When fetching a huggg with a v2 voucher, we need to fetch extra data If the
 * voucher is a new voucher type . (This is not embeddable.)
 *
 * This method takes a fetched-huggg and hydrates it with any voucher details,
 * then returns a promise which resolves to the extended huggg.
 *
 * @param {Object} huggg The huggg to fetch details for.
 * @returns {Promise}
 */
var fetchVoucherV2Details = function fetchVoucherV2Details(axios, huggg) {
  var isTest = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
  var integrationId = path(['purchase', 'brand', 'integration_id'], huggg);
  var isVoucherV2 = integrationId === V2_VOUCHER_INTEGRATION;
  if (isVoucherV2 && huggg.status_id !== hugggStatuses.cancelled) {
    return Promise.all([voucherSegments(axios, huggg.id, isTest), existingvouchers(axios, huggg.id)]).then(function (_ref) {
      var _ref2 = _slicedToArray(_ref, 2),
        productVoucherConfiguration = _ref2[0],
        _ref2$ = _ref2[1],
        segment = _ref2$.segment,
        vouchers = _ref2$.vouchers;
      return _objectSpread(_objectSpread({}, huggg), {}, {
        purchase: _objectSpread(_objectSpread({}, huggg.purchase), {}, {
          items: productVoucherConfiguration.items,
          segments: productVoucherConfiguration.segments
        }),
        productVoucherConfiguration: productVoucherConfiguration,
        vouchers: vouchers,
        redeemedSegment: segment
      });
    });
  }
  return Promise.resolve(huggg);
};

/**
 * Depending on the integration type, makes a call to the appropriate endpoint
 * to generate a voucher. Then reformats that response to a standard format.
 *
 * @param {string} hugggId the huggg id to generate a voucher for
 * @param {string} segmentId the segment id to generate a voucher for
 * @param {int} integrationId the integration type of the huggg purchase's brand
 * @param {object} [data] The data to send with the voucher generation request
 * @param {string} [action] The action to take on the voucher generation request
 */
var generate = function generate(axios, hugggId, segmentId, integrationId, data) {
  var action = arguments.length > 5 && arguments[5] !== undefined ? arguments[5] : 'claim';
  var endpoint = "/hugggs/".concat(hugggId, "/voucher");
  var params = {
    platform: 'app',
    segment: segmentId,
    embed: ['voucher.segment']
  };
  if (integrationId === V2_VOUCHER_INTEGRATION) {
    endpoint = "".concat(endpoint, "/claimed");
    params = {
      segmentId: segmentId,
      data: data,
      action: action
    };
  }
  return axios.post(urls.apiV2(endpoint), params).then(prop('data')).then(generatedVoucherFormatter(integrationId));
};
export default {
  fetchVoucherV2Details: withAxios(fetchVoucherV2Details),
  generate: withAxios(generate),
  reformatSegmentVoucher: reformatSegmentVoucher,
  resendPin4: withAxios(resendPin4)
};